import React, { useEffect, useState } from "react";
import { Cards } from "../components/Cards.tsx";
import { useParams } from "react-router-dom";
import { getServiceUser, updateServiceUser } from "../services/users.js";
import { capitalize, formatUTCForDB, initials } from "../utils/functions.js";
import { buildServiceUserUpdatePayload } from "../utils/serviceUserPayload.js";
import { ServiceUserType } from "../utils/types.ts";
import InputField from "../components/Forms/InputField.js";
import { useForm } from "react-hook-form";
import SelectField from "../components/Forms/SelectField.js";
import Spinner from "../components/PageLoader.js";
import toast from "react-hot-toast";

export const ServiceUserDetails: React.FC = () => {
  const [editContact, setEditContact] = useState(false);
  const [editMedical, setEditMedical] = useState(false);
  const [editNextKin, setEditNextKin] = useState(false);
  const [user, setUser] = useState<ServiceUserType | null>(null);
  const urlParam = useParams();
  const [isLoading, setIsLoading] = useState(false);

  const fetchUser = async () => {
    try {
      const { data } = await getServiceUser(urlParam.id);
      setUser(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {},
  });

  useEffect(() => {
    if (user) {
      // Format in UTC: these are UTC timestamps, and local rendering shifts
      // the calendar day for timezones west of UTC — the shifted date would
      // then be written back on save
      reset({
        ...user,
        dateOfBirth: formatUTCForDB(user?.dateOfBirth, "yyyy-MM-dd") || "",
        startDate: formatUTCForDB(user?.startDate, "yyyy-MM-dd") || "",
      });
    }
  }, [user, reset]);

  const onSubmit = async (e) => {
    setIsLoading(true);
    // Only forward the editable personal-information fields. The form is seeded
    // with the whole user object, and sending non-editable fields (notably the
    // JSONB `calls` field, and empty date strings) made the update fail (500 /
    // unparseable payload) — see buildServiceUserUpdatePayload (CCS-388).
    const payload = buildServiceUserUpdatePayload(e);
    try {
      await updateServiceUser(payload, urlParam.id);
      fetchUser();
      toast.success("User updated successfully");
      setEditContact(false);
      setEditMedical(false);
      setEditNextKin(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {user ? (
        <div className="service-user-details">
          {/* Header Section with Enhanced Design */}
          <Cards classes="mb-24 service-user-details__header">
            <div className="service-user-details__header-content">
              <div className="service-user-details__title-section">
                <div className="service-user-details__icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="white"/>
                    <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="white"/>
                  </svg>
                </div>
                <div>
                  <h4 className="service-user-details__title">Service User Profile</h4>
                  <p className="service-user-details__subtitle">
                    {`${user?.firstName} ${user?.lastName}`} • {formatUTCForDB(user?.startDate, "dd MMMM yyyy") || "--"}
                  </p>
                </div>
              </div>
              <div className="service-user-details__avatar-section">
                <div className="service-user-details__avatar-circle">
                  {initials(user?.firstName, user?.lastName)}
                </div>
                <button className="service-user-details__care-plan-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H12V17H10V12H7V10H10V7H12V10H17V12Z" fill="currentColor"/>
                  </svg>
                  Care Plan
                </button>
              </div>
            </div>
          </Cards>

          {/* Personal Information Section */}
          <Cards classes="mb-24 service-user-details__personal" edit setEdit={() => setEditContact(!editContact)}>
            <div className="service-user-details__section-header">
              <div className="service-user-details__section-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="var(--primary)"/>
                  <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="var(--primary)"/>
                </svg>
              </div>
              <h6>Personal Information</h6>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="service-user-details__info-grid">
                {!editContact ? (
                  <div className="service-user-details__info-item">
                    <label>Date of birth</label>
                    <p className="service-user-details__info-value">
                      {formatUTCForDB(user?.dateOfBirth, "do MMM, yyyy") || "N/A"}
                    </p>
                  </div>
                ) : (
                  <InputField
                    name="dateOfBirth"
                    control={control}
                    label="Date of birth"
                    type="date"
                    error={errors["dateOfBirth"]?.message}
                  />
                )}
                {!editContact ? (
                  <div className="service-user-details__info-item">
                    <label>Phone</label>
                    <p className="service-user-details__info-value">{user?.telephoneNumber || "N/A"}</p>
                  </div>
                ) : (
                  <InputField
                    name="telephoneNumber"
                    control={control}
                    label="Phone number"
                    type="text"
                    error={errors["telephoneNumber"]?.message}
                  />
                )}
                {!editContact ? (
                  <div className="service-user-details__info-item">
                    <label>Email</label>
                    <p className="service-user-details__info-value">{user?.emailAddress || "N/A"}</p>
                  </div>
                ) : (
                  <InputField
                    name="emailAddress"
                    control={control}
                    label="Email"
                    type="email"
                    error={errors["emailAddress"]?.message}
                  />
                )}
                {!editContact ? (
                  <div className="service-user-details__info-item">
                    <label>Address</label>
                    <p className="service-user-details__info-value">{user?.address || "N/A"}</p>
                  </div>
                ) : (
                  <InputField
                    name="address"
                    control={control}
                    label="Address"
                    type="text"
                    error={errors["address"]?.message}
                  />
                )}
                {!editContact ? (
                  <div className="service-user-details__info-item">
                    <label>Postal code</label>
                    <p className="service-user-details__info-value">{user?.postalCode || "N/A"}</p>
                  </div>
                ) : (
                  <InputField
                    name="postalCode"
                    control={control}
                    label="Postal code"
                    type="text"
                    error={errors["postalCode"]?.message}
                  />
                )}
                {!editContact ? (
                  <div className="service-user-details__info-item">
                    <label>Gender</label>
                    <p className="service-user-details__info-value">{capitalize(user?.gender) || "N/A"}</p>
                  </div>
                ) : (
                  <SelectField
                    control={control}
                    label="Gender"
                    name="gender"
                    options={[
                      { label: "Male", value: "male" },
                      { label: "Female", value: "female" },
                      { label: "Other", value: "other" },
                    ]}
                    error={errors["gender"]?.message}
                  />
                )}
                {!editContact ? (
                  <div className="service-user-details__info-item">
                    <label>Ethnicity</label>
                    <p className="service-user-details__info-value">{user?.ethnicity || "N/A"}</p>
                  </div>
                ) : (
                  <InputField
                    name="ethnicity"
                    control={control}
                    label="Ethnicity"
                    type="text"
                    error={errors["ethnicity"]?.message}
                  />
                )}
                {!editContact ? (
                  <div className="service-user-details__info-item">
                    <label>Start date</label>
                    <p className="service-user-details__info-value">
                      {formatUTCForDB(user?.startDate, "do MMM, yyyy") || "N/A"}
                    </p>
                  </div>
                ) : (
                  <InputField
                    name="startDate"
                    control={control}
                    label="Start date"
                    type="date"
                    error={errors["startDate"]?.message}
                  />
                )}
              </div>

              {editContact && (
                <div className="service-user-details__actions">
                  <button
                    onClick={() => setEditContact(false)}
                    type="button"
                    className="service-user-details__action-btn service-user-details__action-btn--secondary"
                  >
                    Close
                  </button>
                  <button
                    className="service-user-details__action-btn service-user-details__action-btn--primary"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              )}
            </form>
          </Cards>

          {/* Next of Kin Section */}
          <Cards classes="mb-24 service-user-details__next-of-kin" edit setEdit={() => setEditNextKin(!editNextKin)}>
            <div className="service-user-details__section-header">
              <div className="service-user-details__section-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M16 4C18.2091 4 20 5.79086 20 8C20 10.2091 18.2091 12 16 12C13.7909 12 12 10.2091 12 8C12 5.79086 13.7909 4 16 4Z" fill="var(--primary)"/>
                  <path d="M8 6C9.65685 6 11 7.34315 11 9C11 10.6569 9.65685 12 8 12C6.34315 12 5 10.6569 5 9C5 7.34315 6.34315 6 8 6Z" fill="var(--primary)"/>
                  <path d="M16 14C12.6863 14 10 16.6863 10 20H22C22 16.6863 19.3137 14 16 14Z" fill="var(--primary)"/>
                  <path d="M8 14C5.79086 14 4 15.7909 4 18V20H12V18C12 15.7909 10.2091 14 8 14Z" fill="var(--primary)"/>
                </svg>
              </div>
              <h6>Next of Kin</h6>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="service-user-details__info-grid">
                {!editNextKin ? (
                  <div className="service-user-details__info-item">
                    <label>Name</label>
                    <p className="service-user-details__info-value">{user?.nextOfKin || "N/A"}</p>
                  </div>
                ) : (
                  <InputField
                    name="nextOfKin"
                    control={control}
                    label="Name"
                    type="text"
                    error={errors["nextOfKin"]?.message}
                  />
                )}
                {!editNextKin ? (
                  <div className="service-user-details__info-item">
                    <label>Phone number</label>
                    <p className="service-user-details__info-value">{user?.nextOfKinContactNumber || "N/A"}</p>
                  </div>
                ) : (
                  <InputField
                    name="nextOfKinContactNumber"
                    control={control}
                    label="Phone number"
                    type="text"
                    error={errors["nextOfKinContactNumber"]?.message}
                  />
                )}
              </div>

              {editNextKin && (
                <div className="service-user-details__actions">
                  <button
                    onClick={() => setEditNextKin(false)}
                    type="button"
                    className="service-user-details__action-btn service-user-details__action-btn--secondary"
                  >
                    Close
                  </button>
                  <button
                    className="service-user-details__action-btn service-user-details__action-btn--primary"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              )}
            </form>
          </Cards>

          {/* Medical Details Section */}
          <Cards classes="mb-24 service-user-details__medical" edit setEdit={() => setEditMedical(!editMedical)}>
            <div className="service-user-details__section-header">
              <div className="service-user-details__section-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" fill="var(--primary)"/>
                </svg>
              </div>
              <h6>Medical Details</h6>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="service-user-details__info-grid">
                {!editMedical ? (
                  <div className="service-user-details__info-item">
                    <label>GP practice</label>
                    <p className="service-user-details__info-value">{user?.gpPractice || "N/A"}</p>
                  </div>
                ) : (
                  <InputField
                    name="gpPractice"
                    control={control}
                    label="GP practice"
                    type="text"
                    error={errors["gpPractice"]?.message}
                  />
                )}
                {!editMedical ? (
                  <div className="service-user-details__info-item">
                    <label>GP contact number</label>
                    <p className="service-user-details__info-value">{user?.gpContactNumber || "N/A"}</p>
                  </div>
                ) : (
                  <InputField
                    name="gpContactNumber"
                    control={control}
                    label="GP contact number"
                    type="text"
                    error={errors["gpContactNumber"]?.message}
                  />
                )}
                {!editMedical ? (
                  <div className="service-user-details__info-item">
                    <label>Pharmacy</label>
                    <p className="service-user-details__info-value">{user?.pharmacy || "N/A"}</p>
                  </div>
                ) : (
                  <InputField
                    name="pharmacy"
                    control={control}
                    label="Pharmacy"
                    type="text"
                    error={errors["pharmacy"]?.message}
                  />
                )}
                {!editMedical ? (
                  <div className="service-user-details__info-item">
                    <label>NFC Tag</label>
                    <p className="service-user-details__info-value">{user?.nfcTag || "N/A"}</p>
                  </div>
                ) : (
                  <InputField
                    name="nfcTag"
                    control={control}
                    label="NFC Tag"
                    type="text"
                    error={errors["nfcTag"]?.message}
                  />
                )}
              </div>

              {editMedical && (
                <div className="service-user-details__actions">
                  <button
                    onClick={() => setEditMedical(false)}
                    type="button"
                    className="service-user-details__action-btn service-user-details__action-btn--secondary"
                  >
                    Close
                  </button>
                  <button
                    className="service-user-details__action-btn service-user-details__action-btn--primary"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              )}
            </form>
          </Cards>
        </div>
      ) : (
        <Spinner />
      )}
    </>
  );
};
